import React, { useState, useEffect } from 'react';
import { CheckCircle2, X, Check, Shield, Phone, Calendar, ArrowRight, TrendingUp, Users, Clock, Zap } from 'lucide-react';
import ConsultationForm from '../components/ConsultationForm';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ExitIntentPopup from '../components/ExitIntentPopup';
import MobilePhoneFAB from '../components/MobilePhoneFAB';
import CookieConsent from '../components/CookieConsent';
import { credentialBadges } from '../constants/credentialBadges';
import '../styles/landing.css';
import '../styles/switch2.css';

const SwitchIIPage = () => {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [exitPopupShown, setExitPopupShown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);
    };

    let pageLoadTime = Date.now();
    const handleMouseLeave = (e) => {
      const timeOnPage = Date.now() - pageLoadTime;
      if (e.clientY <= 0 && timeOnPage > 5000 && !exitPopupShown && !showThankYou && !showExitPopup) {
        setShowExitPopup(true);
        setExitPopupShown(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [showThankYou, exitPopupShown, showExitPopup]);

  const handleFormSubmit = (email) => {
    setSubmittedEmail(email);
    setShowThankYou(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToForm = () => {
    document.getElementById('hero-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (showThankYou) {
    return (
      <div className="landing-page switch2-page">
        <ThankYouSection email={submittedEmail} />
        <Footer credentialBadges={credentialBadges} />
      </div>
    );
  }

  return (
    <div className="landing-page switch2-page">
      <ExitIntentPopup 
        isOpen={showExitPopup} 
        onClose={() => setShowExitPopup(false)}
        onSubmit={() => setShowExitPopup(false)}
      />
      <MobilePhoneFAB />

      {showStickyCTA && (
        <div className="sticky-cta-bar">
          <div className="sticky-cta-content">
            <span className="sticky-cta-text">Ready to make the switch?</span>
            <div className="sticky-cta-actions">
              <a href="tel:+18558088689" className="sticky-phone-link">
                <Phone size={18} />
                (855) 808-8689
              </a>
              <button className="sticky-cta-button" onClick={scrollToForm}>
                Get Your Free Switch Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      <HeroSection onFormSubmit={handleFormSubmit} />
      <TransformSection />
      <SolutionSection />
      <BenefitsSection onFormSubmit={handleFormSubmit} />
      <SwitchProcessSection />
      <SocialProofSection credentialBadges={credentialBadges} />
      <FAQSection />
      <FinalCTASection onFormSubmit={handleFormSubmit} />
      <Footer credentialBadges={credentialBadges} />
      <CookieConsent />
    </div>
  );
};

const HeroSection = ({ onFormSubmit }) => {
  const openCalendly = () => {
    window.Calendly?.initPopupWidget({url: 'https://calendly.com/d/cxnm-px9-pj8/new-meeting'});
  };

  return (
    <section className="hero-section switch2-hero">
      <div className="hero-content">
        <div className="hero-text">
          <div className="hero-badge switch2-badge">
            <Zap size={18} />
            <span>MAKE THE SWITCH</span>
          </div>
          <h1 className="hero-headline">Escape the Big-Box PEO Trap</h1>
          <p className="hero-subheadline">
            Tired of call centers, hidden fees, and reps who don't know your name? 
            Join hundreds of businesses who switched to <span className="emphasis-your">real service</span> and 
            found <span className="emphasis-savings">15-40% savings</span>.
          </p>
          
          <div className="switch2-stats">
            <div className="switch2-stat">
              <span className="switch2-stat-number">30-Day</span>
              <span className="switch2-stat-label">Transition</span>
            </div>
            <div className="switch2-stat-divider"></div>
            <div className="switch2-stat">
              <span className="switch2-stat-number">$0</span>
              <span className="switch2-stat-label">Hidden Fees</span>
            </div>
            <div className="switch2-stat-divider"></div>
            <div className="switch2-stat">
              <span className="switch2-stat-number">1</span>
              <span className="switch2-stat-label">Dedicated Team</span>
            </div>
          </div>
          
          <div className="hero-phone-cta">
            <Phone size={20} />
            <span>Ready now? Call us: </span>
            <a href="tel:+18558088689" className="phone-link">(855) 808-8689</a>
          </div>
          <p className="hero-phone-note">One number. Real people. No runarounds.</p>
        </div>
        <div className="hero-form" id="hero-form">
          <div className="form-trust-badges">
            <div className="trust-badge-item">
              <CheckCircle2 size={16} />
              <span>Certified</span>
            </div>
            <div className="trust-badge-item">
              <CheckCircle2 size={16} />
              <span>Accredited</span>
            </div>
            <div className="trust-badge-item">
              <CheckCircle2 size={16} />
              <span>Award Winning</span>
            </div>
          </div>
          <ConsultationForm onSubmit={onFormSubmit} darkMode source="switch2" />
          <p className="form-trust-line">
            Free switch analysis. See exactly what you'd save.
          </p>
          
          <div className="form-divider">
            <span>OR</span>
          </div>
          <button className="calendly-cta" onClick={openCalendly}>
            <Calendar size={20} />
            Book a 15-minute call
          </button>
        </div>
      </div>
    </section>
  );
};

const TransformSection = () => (
  <section className="switch2-transform">
    <div className="section-container">
      <h2 className="section-headline">Your Current Reality vs. Your Future</h2>
      <p className="section-supporting-text">See the difference a real PEO partner makes</p>
      
      <div className="switch2-transform-grid">
        <div className="switch2-transform-card before">
          <div className="switch2-transform-label">
            <X size={20} />
            <span>WITH YOUR CURRENT PEO</span>
          </div>
          <ul className="switch2-transform-list">
            <li><X size={18} /> Hours on hold with call centers</li>
            <li><X size={18} /> New rep every few months</li>
            <li><X size={18} /> Surprise fees on every invoice</li>
            <li><X size={18} /> Days waiting for simple answers</li>
            <li><X size={18} /> One-size-fits-all benefits</li>
            <li><X size={18} /> "Take it or leave it" renewals</li>
          </ul>
        </div>

        <div className="switch2-transform-arrow">
          <ArrowRight size={48} />
        </div>

        <div className="switch2-transform-card after">
          <div className="switch2-transform-label">
            <Check size={20} />
            <span>WITH AMPLIFY HR</span>
          </div>
          <ul className="switch2-transform-list">
            <li><Check size={18} /> Direct line to YOUR team</li>
            <li><Check size={18} /> Same reps who know your business</li>
            <li><Check size={18} /> Transparent, itemized pricing</li>
            <li><Check size={18} /> Same-day response commitment</li>
            <li><Check size={18} /> Flexible benefits across carriers</li>
            <li><Check size={18} /> We shop renewals for you</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const SolutionSection = () => (
  <section className="solution-section">
    <div className="section-container">
      <h2 className="section-headline">Why Businesses Are Switching</h2>
      <p className="section-intro">
        Stop managing HR through a ticket queue. Amplify HR pairs you with a dedicated team, 
        transparent itemized pricing, and rapid response accountability.
      </p>
      
      <div className="switch2-reasons">
        <div className="switch2-reason">
          <div className="switch2-reason-icon">
            <TrendingUp size={32} />
          </div>
          <h3>15-40% Cost Savings</h3>
          <p>Hidden fees add up. Our transparent pricing means you keep more of what you earn.</p>
        </div>
        <div className="switch2-reason">
          <div className="switch2-reason-icon">
            <Users size={32} />
          </div>
          <h3>Dedicated Team</h3>
          <p>Stop re-explaining your business. Work with the same people who truly know you.</p>
        </div>
        <div className="switch2-reason">
          <div className="switch2-reason-icon">
            <Clock size={32} />
          </div>
          <h3>Same-Day Response</h3>
          <p>No more ticket queues. Real answers from real people, fast.</p>
        </div>
        <div className="switch2-reason">
          <div className="switch2-reason-icon">
            <Shield size={32} />
          </div>
          <h3>Better Benefits</h3>
          <p>Access our FirstAdvantage™ program with zero-deductible health options.</p>
        </div>
      </div>

      <div className="comparison-table">
        <table>
          <thead>
            <tr>
              <th>What Matters</th>
              <th>Big-Box PEOs</th>
              <th>Amplify HR</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Support Model</td>
              <td><div className="table-cell-content"><X size={16} className="table-icon-bad" /><span>Call centers, ticket queues</span></div></td>
              <td><div className="table-cell-content"><Check size={16} className="table-icon-good" /><span>Dedicated team, direct access</span></div></td>
            </tr>
            <tr>
              <td>Pricing</td>
              <td><div className="table-cell-content"><X size={16} className="table-icon-bad" /><span>Hidden fees, surprise charges</span></div></td>
              <td><div className="table-cell-content"><Check size={16} className="table-icon-good" /><span>Transparent, itemized pricing</span></div></td>
            </tr>
            <tr>
              <td>Response Time</td>
              <td><div className="table-cell-content"><X size={16} className="table-icon-bad" /><span>3-4 business days average</span></div></td>
              <td><div className="table-cell-content"><Check size={16} className="table-icon-good" /><span>Same-day commitment</span></div></td>
            </tr>
            <tr>
              <td>Benefits Options</td>
              <td><div className="table-cell-content"><X size={16} className="table-icon-bad" /><span>Single carrier, limited options</span></div></td>
              <td><div className="table-cell-content"><Check size={16} className="table-icon-good" /><span>Multi-carrier, FirstAdvantage™</span></div></td>
            </tr>
            <tr>
              <td>Transition</td>
              <td><div className="table-cell-content"><X size={16} className="table-icon-bad" /><span>Complex, disruptive</span></div></td>
              <td><div className="table-cell-content"><Check size={16} className="table-icon-good" /><span>30-45 days, zero disruption</span></div></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

const BenefitsSection = ({ onFormSubmit }) => (
  <section className="benefits-section switch2-benefits">
    <div className="section-container">
      <div className="switch2-benefits-header">
        <span className="switch2-benefits-eyebrow">THE AMPLIFY ADVANTAGE</span>
        <h2 className="section-headline">What You Gain by Switching</h2>
        <p className="switch2-benefits-intro">Real numbers from real clients who made the move</p>
      </div>
      
      <div className="switch2-benefits-grid">
        <div className="switch2-benefit-card">
          <div className="switch2-benefit-icon savings">
            <TrendingUp size={28} />
          </div>
          <div className="switch2-benefit-stat">15-40%</div>
          <div className="switch2-benefit-label">Average Cost Savings</div>
          <p className="switch2-benefit-desc">Transparent pricing eliminates hidden fees that drain your budget.</p>
        </div>
        <div className="switch2-benefit-card">
          <div className="switch2-benefit-icon time">
            <Clock size={28} />
          </div>
          <div className="switch2-benefit-stat">15+</div>
          <div className="switch2-benefit-label">Hours Saved Per Week</div>
          <p className="switch2-benefit-desc">Stop chasing answers through call centers and ticket queues.</p>
        </div>
        <div className="switch2-benefit-card">
          <div className="switch2-benefit-icon satisfaction">
            <Users size={28} />
          </div>
          <div className="switch2-benefit-stat">97%</div>
          <div className="switch2-benefit-label">Client Satisfaction</div>
          <p className="switch2-benefit-desc">Clients rate our transition process as efficient and non-disruptive.</p>
        </div>
        <div className="switch2-benefit-card">
          <div className="switch2-benefit-icon benefits">
            <Shield size={28} />
          </div>
          <div className="switch2-benefit-stat">$0</div>
          <div className="switch2-benefit-label">Deductible Options</div>
          <p className="switch2-benefit-desc">FirstAdvantage™ benefits that help you attract and retain top talent.</p>
        </div>
      </div>
      
      <div className="benefits-form-container">
        <ConsultationForm onSubmit={onFormSubmit} source="switch2" />
      </div>
    </div>
  </section>
);

const SwitchProcessSection = () => (
  <section className="switch2-process-section">
    <div className="section-container">
      <div className="switch2-process-header">
        <span className="switch2-process-badge">SIMPLE 3-STEP PROCESS</span>
        <h2 className="section-headline">Switching Is Easier Than You Think</h2>
        <p className="switch2-process-subtitle">Most transitions complete in 30-45 days with zero disruption</p>
      </div>
      
      <div className="switch2-process-grid">
        <div className="switch2-process-card">
          <div className="switch2-process-card-glow"></div>
          <div className="switch2-process-number-wrapper">
            <div className="switch2-process-number">1</div>
          </div>
          <div className="switch2-process-icon">
            <TrendingUp size={32} />
          </div>
          <h3 className="switch2-process-title">Free Switch Analysis</h3>
          <p className="switch2-process-desc">We review your current setup and show you exactly what you'd save—no obligation.</p>
          <div className="switch2-process-tag">No commitment required</div>
        </div>

        <div className="switch2-process-connector">
          <ArrowRight size={24} />
        </div>

        <div className="switch2-process-card">
          <div className="switch2-process-card-glow"></div>
          <div className="switch2-process-number-wrapper">
            <div className="switch2-process-number">2</div>
          </div>
          <div className="switch2-process-icon">
            <Shield size={32} />
          </div>
          <h3 className="switch2-process-title">Custom Proposal</h3>
          <p className="switch2-process-desc">Clear pricing, better benefits, dedicated team. Quotes in days, not weeks.</p>
          <div className="switch2-process-tag">Tailored to your business</div>
        </div>

        <div className="switch2-process-connector">
          <ArrowRight size={24} />
        </div>

        <div className="switch2-process-card">
          <div className="switch2-process-card-glow"></div>
          <div className="switch2-process-number-wrapper">
            <div className="switch2-process-number">3</div>
          </div>
          <div className="switch2-process-icon">
            <Zap size={32} />
          </div>
          <h3 className="switch2-process-title">Seamless Transition</h3>
          <p className="switch2-process-desc">We handle the heavy lifting while you focus on your business. Your team won't skip a beat.</p>
          <div className="switch2-process-tag">Zero disruption guaranteed</div>
        </div>
      </div>

      <div className="switch2-process-cta">
        <p className="switch2-process-cta-text">Ready to see what you could save?</p>
        <button className="switch2-process-cta-btn" onClick={() => document.getElementById('hero-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
          Get Your Free Analysis <ArrowRight size={18} />
        </button>
      </div>
    </div>
  </section>
);

const SocialProofSection = ({ credentialBadges }) => (
  <section className="social-proof-section">
    <div className="section-container">
      <h2 className="section-headline">Real Results from Real Switchers</h2>
      
      <div className="testimonials-scroll">
        <div className="testimonials-track">
          <TestimonialCard
            quote="We switched from a major PEO and immediately noticed the difference. Real people who answer the phone, know our name, and actually solve problems."
            author="MW"
            company="Technology Services, 44 employees"
          />
          <TestimonialCard
            quote="After 7 years, we're still dealing with the same key team members. That consistency is invaluable—they understand our business history."
            author="MW"
            company="Professional Services, 117 employees"
          />
          <TestimonialCard
            quote="Amplify HR expertly manages all the day-to-day functions of an enterprise-level HR department, allowing us to focus on our core business."
            author="YB"
            company="Professional Services, 72 employees"
          />
          <TestimonialCard
            quote="We came from another PEO and Amplify HR was able to provide us with better insurance prices, lower admin fees and superior service."
            author="HB"
            company="Professional Services, 25 employees"
          />
          <TestimonialCard
            quote="Connor and his team showed us that they could help reduce our administrative burdens while providing us with excellent service."
            author="LG"
            company="Marketing Company, 27 employees"
          />
        </div>
      </div>

      <div className="credential-badges">
        {credentialBadges.map((badge) => (
          <img key={badge.alt} src={badge.src} alt={badge.alt} className="credential-badge-img" loading="lazy" />
        ))}
      </div>
    </div>
  </section>
);

const TestimonialCard = ({ quote, author, company }) => (
  <div className="testimonial-card">
    <p className="testimonial-quote">"{quote}"</p>
    <div className="testimonial-author">
      <div className="testimonial-avatar">{author}</div>
      <div className="testimonial-info">
        <span className="testimonial-name">{author}</span>
        <span className="testimonial-company">{company}</span>
      </div>
    </div>
  </div>
);

const FAQSection = () => (
  <section className="faq-section">
    <div className="section-container">
      <h2 className="section-headline">Common Questions About Switching</h2>
      <div className="faq-container">
        <Accordion type="single" collapsible className="faq-accordion">
          <AccordionItem value="item-1">
            <AccordionTrigger>How long does the switch take?</AccordionTrigger>
            <AccordionContent>
              Most transitions complete in 30-45 days. We handle the coordination with your current provider, benefits enrollment, and payroll migration. 97% of clients report the transition is efficient and non-disruptive.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-2">
            <AccordionTrigger>Will my employees notice the change?</AccordionTrigger>
            <AccordionContent>
              The only thing your employees will notice is better service. Benefits continue uninterrupted, payroll stays on schedule, and they gain access to better support and resources.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-3">
            <AccordionTrigger>Can I keep my current benefits?</AccordionTrigger>
            <AccordionContent>
              Yes. You can keep your current plans and have Amplify administer them, adopt Amplify plans like FirstAdvantage™ with $0 deductibles, or choose a hybrid approach.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-4">
            <AccordionTrigger>What if I have a contract with my current PEO?</AccordionTrigger>
            <AccordionContent>
              Most PEO contracts allow 30-60 day notice to exit. We'll review your current agreement and help you understand your options. Many clients are surprised to learn switching is easier than they expected.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-5">
            <AccordionTrigger>How much will I actually save?</AccordionTrigger>
            <AccordionContent>
              Clients typically save 15-40% on total PEO costs by eliminating hidden fees and accessing better rates. Your free switch analysis will show exactly what you'd save based on your current setup.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  </section>
);

const FinalCTASection = ({ onFormSubmit }) => {
  const openCalendly = () => {
    window.Calendly?.initPopupWidget({url: 'https://calendly.com/d/cxnm-px9-pj8/new-meeting'});
  };

  return (
    <section className="final-cta-section">
      <div className="section-container">
        <div className="final-cta-content">
          <h2 className="final-cta-headline">Every Day with Your Current PEO Is Costing You</h2>
          <p className="final-cta-subheadline">
            Hidden fees. Wasted time. Employee frustration. See exactly what you'd save by switching.
          </p>
          
          <div className="final-cta-form">
            <ConsultationForm onSubmit={onFormSubmit} darkMode source="switch2" />
          </div>
          
          <div className="final-cta-alternatives">
            <div className="final-cta-divider"><span>OR</span></div>
            <div className="final-cta-options">
              <a href="tel:+18558088689" className="final-cta-phone">
                <Phone size={20} />
                <span>(855) 808-8689</span>
              </a>
              <button className="final-cta-calendly" onClick={openCalendly}>
                <Calendar size={20} />
                <span>Schedule a Call</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ThankYouSection = ({ email }) => (
  <section className="thankyou-section">
    <div className="section-container">
      <div className="thankyou-icon">
        <CheckCircle2 size={80} />
      </div>
      <h1 className="thankyou-headline">You're One Step Closer to Better HR</h1>
      <p className="thankyou-text">
        Thanks! An Amplify HR specialist will contact you within 24 hours with your personalized switch analysis.
      </p>
      <div className="thankyou-next-steps">
        <h3>What Happens Next:</h3>
        <ol>
          <li>We review your current PEO setup and costs</li>
          <li>We prepare a detailed savings analysis</li>
          <li>You receive a custom proposal with zero obligation</li>
        </ol>
      </div>
    </div>
  </section>
);

const Footer = ({ credentialBadges }) => (
  <footer className="footer switch2-footer">
    <div className="footer-content">
      <div className="switch2-footer-badges">
        {credentialBadges.map((badge) => (
          <div key={badge.alt} className="switch2-footer-badge">
            <img src={badge.src} alt={badge.alt} loading="lazy" />
          </div>
        ))}
      </div>
      <p className="footer-copyright">© 2026 Amplify HR. All rights reserved.</p>
    </div>
  </footer>
);

export default SwitchIIPage;
